import { MenuItem } from '@mui/material';
import moment, { Moment } from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { Gender } from '../../../../shared/api/hscan/account';
import {
  Case,
  UploadImagesBody,
  uploadImagesMandatoryKeys,
  UploadProgress,
  validateUploadImagesBody,
} from '../../../../shared/api/hscan/case';
import { validateUploadParamValue } from '../../../../shared/api/hscan/types';
import { useUserState } from '../../../../shared/hooks/accountHook';
import {
  useForm,
  useUploadTrayProgressNotifier,
} from '../../../../shared/hooks/caseHook';
import { NonOptionalKeys } from '../../../../shared/utils';
import { CodedEntry } from '../../../../shared/utils/bodyPart';
import { ISO_LOCAL_DATE } from '../../../../shared/utils/dateTime';
import birthIcon from '../../assets/icons/icon_birth.svg';
import bodypartIcon from '../../assets/icons/icon_bodypart.svg';
import calendarIcon from '../../assets/icons/icon_calendar.svg';
import genderIcon from '../../assets/icons/icon_gender.svg';
import hospitalIcon from '../../assets/icons/icon_hospital.svg';
import noteIcon from '../../assets/icons/icon_note.svg';
import peopleIcon from '../../assets/icons/icon_people.svg';
import symptomIcon from '../../assets/icons/icon_symptom.svg';
import { BlueRoundedButtonWithIcon } from '../../common/components/BlueRoundedButton';
import { CheckboxWithText } from '../../common/components/CheckboxWithText';
import { ColoredIcon } from '../../common/components/Icon';
import {
  BodyPartPickerSlot,
  DateFieldSlot,
  ImageUploadSlot,
  MultiLineTextFieldSlot,
  SelectSlot,
  TextFieldSlot,
} from '../../common/components/Slot';
import {
  SlotGrid,
  SlotGridWrapper,
} from '../../common/components/Slot/SlotWrapper';
import { SingleFile } from '../../hooks/fileDndHook';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

type InvalidState = Record<NonOptionalKeys<UploadImagesBody>, boolean> & {
  imageUpload: boolean;
};
const initialInvalidState: InvalidState = {
  imageUpload: false,
  patientId: false,
  patientName: false,
  patientGender: false,
  birthDate: false,
  symptom: false,
  injuryLocation: false,
  studyDate: false,
};

const translateToI18nId = (key: keyof InvalidState) =>
  key === 'patientName'
    ? 'name'
    : key === 'studyDate'
    ? 'imageDate'
    : key === 'injuryLocation'
    ? 'bodypart'
    : key === 'patientGender'
    ? 'gender'
    : key;

const CheckboxText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: var(--HScan_dark_blue, #16172c);
`;

const ImageTabSlotGrid = styled(SlotGrid)`
  row-gap: 24px;
`;

export const UploadImageTab = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.uploadImages',
  });
  const [loadUserInfo, setLoadUserInfo] = useState(true);

  const windowSize = useContext(WindowSizeContext);
  const isMobile = windowSize === WindowSize.MOBILE;

  // form state
  const [user] = useUserState();
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState<Moment | null>(null);
  const [gender, setGender] = useState('');
  const [id, setId] = useState(uuidv4());
  const { onStart, onProgress, onComplete, onError } =
    useUploadTrayProgressNotifier(id);
  useEffect(() => {
    if (user && loadUserInfo) {
      setName(user.name);
      setBirthDate(moment(user.birthDate));
      setGender(user.gender);
    }
  }, [user, loadUserInfo]);

  const [institutionName, setInstitutionName] = useState('');
  const [symptom, setSymptom] = useState('');
  const [injuryLocation, setInjuryLocation] = useState<
    CodedEntry | undefined
  >();
  const [imageDate, setImageDate] = useState<Moment | null>(null);
  const [description, setDescription] = useState('');

  const [singleFiles, setSingleFiles] = useState<SingleFile[] | undefined>();
  const files = useMemo(
    () => singleFiles?.map(file => file.file) ?? [],
    [singleFiles],
  );
  const resetFiles = () => {
    setSingleFiles([]);
  };

  // ui
  const [invalidState, setInvalidState] = useState(initialInvalidState);
  const updateProgress = useCallback(
    (uploadProgress: UploadProgress) => {
      const { loaded, total } = uploadProgress;
      const currentPercentage = loaded / (total as number);
      onProgress(currentPercentage);
    },
    [onProgress],
  );

  const onUploadFormSuccess = (data: Case[]) => {
    if (!data.length) {
      onError(t('modal.fail'));
      return;
    }
    onComplete();
    resetFiles();
  };
  const onUploadFormError = () => {
    onError(t('modal.fail'));
  };

  const {
    mutate: upload,
    isSuccess,
    isPending: isUploading,
  } = useForm(
    {
      onSuccess: onUploadFormSuccess,
      onError: onUploadFormError,
    },
    updateProgress,
  );

  const onClickButton = () => {
    const form: Partial<UploadImagesBody> = {
      patientId: user?.userId ?? '',
      patientName: name,
      patientGender: gender,
      birthDate: birthDate?.format(ISO_LOCAL_DATE) ?? '',
      symptom,
      injuryLocation,
      institutionName,
      description,
      studyDate: imageDate?.format(ISO_LOCAL_DATE) ?? '',
    };

    setInvalidState(() => initialInvalidState);

    if (files.length === 0 || !validateUploadImagesBody(form)) {
      const missingKeys: (keyof InvalidState)[] = [
        ...(files.length === 0
          ? (['imageUpload'] as Array<'imageUpload'>)
          : []),
        ...uploadImagesMandatoryKeys.filter(
          key => !validateUploadParamValue(form[key]),
        ),
      ];
      setInvalidState(prev => {
        const newState = { ...prev };
        missingKeys.forEach(key => {
          newState[key] = true;
        });
        return newState;
      });

      const message = `${t('modal.missingMandatoryField', {
        missingFields: missingKeys
          .map(key => t(`slots.${translateToI18nId(key)}.title`))
          .join(', '),
      })}`;
      // TODO: error handling
      onError(message);
      return;
    }

    upload({ form, files });
    onStart();
  };

  const symptomList = {
    hurt: `${t('slots.symptom.options.0')}`,
    burnt: `${t('slots.symptom.options.1')}`,
    surgery: `${t('slots.symptom.options.2')}`,
    skinDisease: `${t('slots.symptom.options.3')}`,
  };

  // initialize slots when image uploaded
  useEffect(() => {
    if (isSuccess) {
      setLoadUserInfo(true);
      setInstitutionName('');
      setSymptom('');
      setInjuryLocation(undefined);
      setImageDate(null);
      setDescription('');
      setId(uuidv4());
    }
  }, [isSuccess]);

  const onChangeCheckbox = (check: boolean) => {
    setLoadUserInfo(check);
    if (!check) {
      setName('');
      setBirthDate(null);
      setGender('');
    }
  };

  return (
    <>
      <CheckboxWithText
        $alignSelf="flex-end"
        text={<CheckboxText>{t('loadUserInfoButton')}</CheckboxText>}
        value={loadUserInfo}
        onChange={onChangeCheckbox}
      />
      <SlotGridWrapper>
        <ImageTabSlotGrid $marginTop="24px" $width={394} $columnGap={27}>
          <ImageUploadSlot
            label={`${t('slots.imageUpload.title')}:`}
            placeholder={
              isMobile
                ? `${t('slots.imageUpload.mobilePlaceholder')}`
                : `${t('slots.imageUpload.placeholder')}`
            }
            extension={
              isMobile
                ? undefined
                : `(*.bmp, *.jpeg, *.jpg, *.png, *.tiff, *.tif ${t(
                    'slots.imageUpload.etc',
                  )})`
            }
            updateFiles={setSingleFiles}
            files={singleFiles}
            mergedCell
            invalid={invalidState.imageUpload}
          />
          <TextFieldSlot
            label={`${t('slots.name.title')}:`}
            textAlign="center"
            placeholder={`${t('slots.name.placeholder')}`}
            value={name}
            onChange={value => {
              setName(value);
              setLoadUserInfo(false);
            }}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={peopleIcon}
                imageWidth="20px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            invalid={invalidState.patientName}
          />
          <DateFieldSlot
            label={`${t('slots.birthDate.title')}:`}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={birthIcon}
                imageWidth="24px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            date={birthDate}
            onChangeDate={value => {
              setBirthDate(value);
              setLoadUserInfo(false);
            }}
            invalid={invalidState.birthDate}
          />
          <SelectSlot
            label={`${t('slots.gender.title')}:`}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={genderIcon}
                imageWidth="20px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            invalid={invalidState.patientGender}
            value={gender}
            onChange={value => {
              const gender = value as Gender;
              if (Object.values(Gender).includes(gender)) {
                setGender(gender);
                setLoadUserInfo(false);
              }
            }}>
            <MenuItem value={''}>{`${t('slots.gender.placeholder')}`}</MenuItem>
            <MenuItem value={Gender.M}>{`${t(
              'slots.gender.options.0',
            )}`}</MenuItem>
            <MenuItem value={Gender.F}>{`${t(
              'slots.gender.options.1',
            )}`}</MenuItem>
            <MenuItem value={Gender.O}>{`${t(
              'slots.gender.options.2',
            )}`}</MenuItem>
          </SelectSlot>
          <TextFieldSlot
            label={`${t('slots.hospital.title')}:`}
            textAlign="center"
            placeholder={`${t('slots.hospital.placeholder')}`}
            value={institutionName}
            onChange={value => setInstitutionName(value)}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={hospitalIcon}
                imageWidth="20px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
          />
          <SelectSlot
            label={`${t('slots.symptom.title')}:`}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={symptomIcon}
                imageWidth="10px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            invalid={invalidState.symptom}
            value={symptom}
            onChange={value => setSymptom(value)}>
            <MenuItem value={''}>{`${t(
              'slots.symptom.placeholder',
            )}`}</MenuItem>
            <MenuItem value={symptomList.hurt}>{symptomList.hurt}</MenuItem>
            <MenuItem value={symptomList.burnt}>{symptomList.burnt}</MenuItem>
            <MenuItem value={symptomList.surgery}>
              {symptomList.surgery}
            </MenuItem>
            <MenuItem value={symptomList.skinDisease}>
              {symptomList.skinDisease}
            </MenuItem>
          </SelectSlot>
          <BodyPartPickerSlot
            label={`${t('slots.bodypart.title')}:`}
            icon={
              <ColoredIcon
                width="24px"
                height="33px"
                imageUrl={bodypartIcon}
                imageWidth="20px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            invalid={invalidState.injuryLocation}
            selected={injuryLocation}
            onSelected={setInjuryLocation}
          />
          <DateFieldSlot
            label={`${t('slots.imageDate.title')}:`}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={calendarIcon}
                imageWidth="18px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            date={imageDate}
            onChangeDate={value => setImageDate(value)}
            invalid={invalidState.studyDate}
          />
          <MultiLineTextFieldSlot
            label={`${t('slots.description.title')}:`}
            textAlign="justify"
            fullwidth
            placeholder={`${t('slots.description.placeholder')}`}
            value={description}
            onChange={value => setDescription(value)}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={noteIcon}
                imageWidth="24px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            mergedCell
          />
        </ImageTabSlotGrid>
      </SlotGridWrapper>
      <BlueRoundedButtonWithIcon
        maxWidth="154px"
        marginTop="36px"
        disabled={isUploading}
        onClick={onClickButton}>
        {t('uploadImageButton')}
      </BlueRoundedButtonWithIcon>
    </>
  );
};
